/* eslint-disable no-debugger */
import { useState, useEffect } from 'react';
import useForm from '@usas/ui-web/utils/UseForm';
import validate from "./ClubExcellenceDetailReportValidate";
import useReportPeriodFlatData from '@usas/ui-web/state/reportPeriodFlat/UseReportPeriodFlatData';
import Constants from '@usas/ui-web/utils/Constants';

import useOrgUnitData from '../../../state/orgUnit/UseOrgUnitData';
import useOrgRoleData from '@usas/ui-web/state/orgRole/UseOrgRoleData';

// Define the columns configuration for this report
const columns = [
  { width: "90px", id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { width: "140px", id: "FirstName", field: "firstName", title: "First Name" },
  { width: "140px", id: "MiddleName", field: "middleName", title: "Middle Name" },
  { width: "140px", id: "LastName", field: "lastName", title: "Last Name" },
  { width: "170px", id: "MemberId", field: "memberId", title: "Member ID" },
  { width: "170px", id: "EventGender", field: "eventGender", title: "Competition Category" },
  { width: "90px", id: "LscCode", field: "lscCode", title: "LSC" },
  { width: "110px", id: "ClubCode", field: "clubCode", title: "Club Code" },
  { width: "360px", id: "ClubName", field: "clubName", title: "Club Name" }
];

const excelColumns = [
  { id: "SeasonYear", field: "seasonYear", title: "Season Year" },
  { id: "FirstName", field: "firstName", title: "First Name" },
  { id: "MiddleName", field: "middleName", title: "Middle Name" },
  { id: "LastName", field: "lastName", title: "Last Name" },
  { id: "MemberId", field: "memberId", title: "Member ID" },
  { id: "EventGender", field: "eventGender", title: "Competition Category" },  
  { id: "LscCode", field: "lscCode", title: "LSC" },
  { id: "ClubCode", field: "clubCode", title: "Club Code" },
  { id: "ClubName", field: "clubName", title: "Club Name" },
  { id: "ClubExcellence_EventCode", field: "clubExcellenceDetail_eventCode", title: "Event Code" },
  { id: "ClubExcellence_SwimTime", field: "clubExcellenceDetail_swimTime", title: "Swim Time" },
  { id: "ClubExcellence_SwimDate", field: "clubExcellenceDetail_swimDate", title: "Swim Date" },
  { id: "ClubExcellence_Points", field: "clubExcellenceDetail_points", title: "Points" },
  { id: "ClubExcellence_PersonPercentTotalPoints", field: "clubExcellenceDetail_personPercentTotalPoints", title: "Percent Total Points" },
  { id: "ClubExcellence_Medal", field: "clubExcellenceDetail_medal", title: "Medal" },
  { id: "ClubExcellence_Rank", field: "clubExcellenceDetail_rank", title: "Rank" }  
];

const REPORT_INFO_ID = 290;

const useClubExcellenceDetailReport = () => {
  const { reportPeriodFlatState, getReportPeriodsFlatByOrganizationId } = useReportPeriodFlatData();
  const [ seasonYearState, setSeasonYearState ] = useState({ isLoading: true, isLoaded: false, options: [] });
  const [ modalState, setModalState ] = useState(getInitialModalState());

  const { orgUnitState } = useOrgUnitData();
  const { orgRoleState } = useOrgRoleData();

  const {
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    updateFormState,
    setFormState,
    setIsDirty
  } = useForm(getInitialFormState(), submitFormCallback, validate);

  function submitFormCallback() {
    setModalState({ ...modalState, displayPopUp: true });
  }

  const onRunReportClicked = (e) => {
    handleSubmit(e);
  }

  const onClearForm = () => {
    setFormState(getInitialFormState());
    setIsDirty(true);
  }

  const onModalCancelClicked = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsDirty(true);
    setModalState({ ...modalState, displayPopUp: false });
  };

  function getInitialModalState() {
    return {
      displayPopUp: false,
      currentPrimaryInfo: '',
      modalTitle: 'Report Viewer'
    };
  }

  function getInitialFormState() {
    return {
      orgUnitId: '',
      seasonId: Constants.DEFAULT_ID,
      seasonYear: Constants.DEFAULT_ID,
      genders: [{ id: 1, name: 'Female' }, { id: 2, name: 'Male' }],
      clubs: [],
      lscs: [],
      zones: [],
      events: [],
      courses: [],
      medals: [],
      exportDetailData: false
    };
  }

  useEffect(() => {
    if (reportPeriodFlatState.isLoading === false && reportPeriodFlatState.isLoaded === false) {
      getReportPeriodsFlatByOrganizationId(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportPeriodFlatState.isLoaded === true) {
      const years = [];

      reportPeriodFlatState.dataAsOptionsWithNames.map(rp => {
        if (rp.name.substring(5, 6) === '(') {
          years.unshift({ id: rp.id, name: rp.name.substring(0, 4) });
        }
      });

      let seasonYear = years.length > 0 ? parseInt(years[0].name, 10) : Constants.DEFAULT_ID;
      seasonYear++;
      years.unshift({ id: 999, name: seasonYear.toString() });
      years.unshift({ id: -1, name: '--' });
      setSeasonYearState({ isLoading: false, isLoaded: true, options: years });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodFlatState]);

  function getClubFileterIds() {
    if (orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 2) { 
      return formState.orgUnitId.toString();
    } else  {
      return formState.lscs.map(c => c.id).join(',')
    } 
  }

  return {
    formState,
    errorState,
    updateFormState,
    reportParameters: {
      SeasonYear_Int: formState.seasonYear === Constants.DEFAULT_ID ? null : parseInt(formState.seasonYear, 10),
      Clubs: formState.clubs.map(club => club.id).join(',') || null, 
      LSCs: formState.lscs.map(lsc => lsc.id).join(',') || null, 
      Zones: formState.zones.map(zone => zone.id).join(',').length === 0 ? null : formState.zones.map(zone => zone.id).join(','),
      Genders: formState.genders.map(gender => gender.id).join(','), 
      Events: formState.events.map(event => event.id).join(',') || null,
      Medals: formState.medals.map(medal => medal.name).join(',') || null,
      OrgUnitId_Int: formState.orgUnitId === "" ? null : formState.orgUnitId,
    },
    parameters: formState,
    columns: columns,
    modalState,
    orgUnitState,
    orgRoleState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns,
    clubFilterIds: getClubFileterIds(),
    REPORT_INFO_ID: REPORT_INFO_ID
  };
};

export default useClubExcellenceDetailReport;